import type { FC } from 'react';
import { createContext, useCallback, useState } from 'react';
import type { BoxProps } from '@mui/material';
import { Box } from '@mui/material';

import AccordionItemHeader from 'src/components/common/Accordion/components/AccordionItemHeader';

import AccordionItem from './components/AccordionItem';
import AccordionItemPanel from './components/AccordionItemPanel';

import { styles } from './index.styles';

export interface AccordionContextValue {
  activeItems: string[];
  level: number;
  toggleActiveState: (itemName: string, level?: number) => void;
}

export const AccordionContext = createContext<AccordionContextValue>({
  activeItems: [],
  level: 0,
  toggleActiveState: () => {},
});

export interface AccordionProps extends BoxProps {
  initiallyActiveItems?: string[];
}

const Accordion: FC<AccordionProps> = ({
  children,
  initiallyActiveItems = [],
  sx = [],
  ...props
}) => {
  const [activeItems, setActiveItems] =
    useState<string[]>(initiallyActiveItems);

  const toggleActiveState = useCallback((itemName: string, level = 0) => {
    setActiveItems((prev) => {
      if (prev[level] === itemName) {
        return prev.slice(0, level);
      }

      return [...prev.slice(0, level), itemName];
    });
  }, []);

  return (
    <AccordionContext.Provider
      value={{
        activeItems,
        level: 0,
        toggleActiveState,
      }}
    >
      <Box
        sx={[styles.accordion, ...(Array.isArray(sx) ? sx : [sx])]}
        {...props}
        className="Accordion-root"
      >
        {children}
      </Box>
    </AccordionContext.Provider>
  );
};

export { AccordionItem, AccordionItemHeader, AccordionItemPanel };

export default Accordion;
